if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.deferredMediaObserver = null;
        this.deferredThumbnailObserver = null;
        this.thumbnailSpacingObserver = null;
        this.updateMediaLayoutHandler = this.updateMediaLayout.bind(this);
        this.handleContentChanged = this.handleContentChanged.bind(this);
        this.handlePageShow = this.handlePageShow.bind(this);
      }

      connectedCallback() {
        this.addEventListener('media-gallery:content-changed', this.handleContentChanged);
        if (this.dataset.context === 'main-product') {
          window.addEventListener('pageshow', this.handlePageShow);
          this.init();
          return;
        }
        this.stopInViewObserver = FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      disconnectedCallback() {
        this.removeEventListener('media-gallery:content-changed', this.handleContentChanged);
        window.removeEventListener('pageshow', this.handlePageShow);
        if (typeof this.stopInViewObserver === 'function') this.stopInViewObserver();
        this.stopInViewObserver = null;
        if (this.mediaQuery?.removeEventListener) {
          this.mediaQuery.removeEventListener('change', this.updateMediaLayoutHandler);
        } else {
          this.mediaQuery?.removeListener?.(this.updateMediaLayoutHandler);
        }
        this.disconnectDeferredMediaObserver();
        this.disconnectDeferredThumbnailObserver();
        this.disconnectThumbnailSpacingObserver();
        this.destroySlider();
        this.destroyImageZoom();
        this.initialized = false;
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        this.mediaQuery = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        if (this.mediaQuery.addEventListener) {
          this.mediaQuery.addEventListener('change', this.updateMediaLayoutHandler);
        } else {
          this.mediaQuery.addListener(this.updateMediaLayoutHandler);
        }
        this.updateMediaLayout();
        this.preloadAllGalleryImages();
        this.observeThumbnailSpacing();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = Number.parseInt(this.dataset.mediaItemGap, 10) || 0;
        const mediaItemGapMobile = Number.parseInt(this.dataset.mediaItemGapMobile, 10) || 0;
        const mobileThumbnailGap = this.getMobileThumbnailGap();

        this.sliderOptions = {
          init: false,
          initialSlide: 0,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          a11y: {
            nextSlideMessage: this.dataset.nextSlideLabel || 'Next slide',
            prevSlideMessage: this.dataset.previousSlideLabel || 'Previous slide',
          },
          // Keep the active thumbnail synchronized when a horizontal trackpad
          // gesture changes the product image. Vertical wheel input remains
          // available for normal page scrolling.
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: true,
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 'auto',
          // Keep spacing in Swiper's geometry instead of CSS flex-gap. A CSS
          // gap is not included in Swiper's maximum translate calculation and
          // can leave the final thumbnail beyond the reachable track end.
          spaceBetween: mobileThumbnailGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          // A conventional mouse wheel should move this horizontal strip as
          // well as a trackpad. Releasing at either end prevents a scroll trap.
          mousewheel: {
            enabled: true,
            forceToAxis: false,
            releaseOnEdges: true,
            thresholdDelta: 6,
          },
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: 16,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: 16,
              },
            };
            break;
          case 'carousel':
            break;
        }
      }

      updateMediaLayout() {
        const useSlider = (this.mediaQuery?.matches ?? FoxTheme.config.mqlMobile) || this.enableDesktopSlider;

        if (useSlider) {
          this.disconnectDeferredMediaObserver();
          this.initSlider();
          this.hydrateActiveMedia(this.sliderInstance?.slider);
        } else {
          this.destroySlider();
          this.observeDeferredMedia();
        }

        this.observeDeferredThumbnails();
        this.syncMobileThumbnailGap();
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions, [
              FoxTheme.Swiper.Mousewheel,
            ]);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
            FoxTheme.Swiper.Mousewheel,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      getMobileThumbnailGap() {
        const sharedOpticalGap = Number.parseFloat(
          window.getComputedStyle(this).getPropertyValue('--mlp-mobile-product-optical-gap')
        );
        if (Number.isFinite(sharedOpticalGap) && sharedOpticalGap > 0) return sharedOpticalGap;

        const galleryWidth = this.getBoundingClientRect().width || this.clientWidth || document.documentElement.clientWidth;
        return Math.max(0, galleryWidth * 0.03);
      }

      syncMobileThumbnailGap() {
        if (window.innerWidth >= 768) return;

        const slider = this.thumbsInstance?.slider;
        if (!slider) return;

        const gap = this.getMobileThumbnailGap();
        if (Math.abs(Number(slider.params.spaceBetween) - gap) < 0.1) return;

        slider.params.spaceBetween = gap;
        slider.update();
      }

      observeThumbnailSpacing() {
        if (!this.elements?.thumbnails || !('ResizeObserver' in window)) return;

        this.disconnectThumbnailSpacingObserver();
        this.thumbnailSpacingObserver = new ResizeObserver(() => this.syncMobileThumbnailGap());
        this.thumbnailSpacingObserver.observe(this);
      }

      disconnectThumbnailSpacingObserver() {
        this.thumbnailSpacingObserver?.disconnect();
        this.thumbnailSpacingObserver = null;
      }

      handlePageShow() {
        if (this.dataset.context !== 'main-product') return;
        requestAnimationFrame(() => {
          if (this.isConnected) this.resetToCover();
        });
      }

      resetToCover() {
        const slider = this.sliderInstance?.slider;
        if (!slider?.slides?.length) return;

        this.hydrateMediaItem(slider.slides[0]);
        this.hydrateNearbyThumbnails(0);
        if (slider.activeIndex !== 0) slider.slideTo(0, 0, false);
        this.updateThumbnailState(0);
      }

      hydrateDeferredImage(image, stateKey = 'deferredProductMedia') {
        if (!image || image.dataset[stateKey] !== 'pending') return;

        const src = image.dataset.deferredSrc;
        const srcset = image.dataset.deferredSrcset;
        const sizes = image.dataset.deferredSizes;
        const eagerSizes = sizes?.replace(/^\s*auto\s*,\s*/i, '');
        if (!src && !srcset) return;

        image.dataset[stateKey] = 'loading';
        image.classList.remove('no-js-hidden');
        image.loading = 'eager';

        const wrapper = image.closest('.media-wrapper');
        wrapper?.classList.remove('loaded', 'error');
        wrapper?.classList.add('loading');

        let settled = false;
        const isRealImageReady = () =>
          image.complete &&
          image.naturalWidth > 0 &&
          Boolean(image.currentSrc) &&
          !image.currentSrc.startsWith('data:');
        const cleanup = () => {
          image.removeEventListener('load', handleLoad);
          image.removeEventListener('error', handleError);
        };
        const markLoaded = () => {
          if (settled) return;
          settled = true;
          cleanup();
          image.dataset[stateKey] = 'loaded';
          image.classList.add('loaded');
          wrapper?.classList.remove('loading', 'error');
          wrapper?.classList.add('loaded');
        };
        const markError = () => {
          if (settled) return;
          if (isRealImageReady()) {
            markLoaded();
            return;
          }
          if (!image.currentSrc || image.currentSrc.startsWith('data:')) return;
          settled = true;
          cleanup();
          image.dataset[stateKey] = 'error';
          image.classList.remove('loaded');
          wrapper?.classList.remove('loading', 'loaded');
          wrapper?.classList.add('error');
        };
        const settleIfReady = () => {
          if (!isRealImageReady()) return false;
          markLoaded();
          return true;
        };
        const decodeAndSettle = () => {
          if (settleIfReady() || typeof image.decode !== 'function') return;
          image
            .decode()
            .then(() => {
              settleIfReady();
            })
            .catch(() => {
              requestAnimationFrame(settleIfReady);
            });
        };
        const handleLoad = () => {
          if (!settleIfReady()) decodeAndSettle();
        };
        const handleError = () => {
          markError();
        };

        image.addEventListener('load', handleLoad);
        image.addEventListener('error', handleError);

        if (eagerSizes) image.setAttribute('sizes', eagerSizes);
        if (srcset) image.setAttribute('srcset', srcset);
        if (src) image.setAttribute('src', src);

        // Reveal immediately after the real sources are attached. Some browsers
        // do not dispatch a second load event when replacing an already-complete
        // inline placeholder, which otherwise leaves the theme's opacity gate at 0.
        image.classList.add('loaded');
        wrapper?.classList.remove('loading', 'error');
        wrapper?.classList.add('loaded');

        image.removeAttribute('data-deferred-src');
        image.removeAttribute('data-deferred-srcset');
        image.removeAttribute('data-deferred-sizes');

        decodeAndSettle();
      }

      hydrateMediaItem(mediaItem) {
        if (!mediaItem) return;
        mediaItem.querySelectorAll('img[data-deferred-product-media="pending"]').forEach((image) => {
          this.hydrateDeferredImage(image);
        });
      }

      hydrateActiveMedia(swiper) {
        if (!swiper?.slides?.length) return;

        const activeIndex = Number.isInteger(swiper.activeIndex) ? swiper.activeIndex : 0;
        const firstIndex = Math.max(0, activeIndex - 1);
        const lastIndex = Math.min(swiper.slides.length - 1, activeIndex + 1);
        for (let index = firstIndex; index <= lastIndex; index += 1) {
          this.hydrateMediaItem(swiper.slides[index]);
        }

        this.hydrateNearbyThumbnails(activeIndex);
      }

      hydrateThumbnailItem(thumbnailItem) {
        if (!thumbnailItem) return;
        thumbnailItem
          .querySelectorAll('img[data-deferred-product-thumbnail="pending"]')
          .forEach((image) => this.hydrateDeferredImage(image, 'deferredProductThumbnail'));
      }

      hydrateNearbyThumbnails(activeIndex = 0) {
        const thumbnailItems = Array.from(this.querySelectorAll('.product__thumbs-item'));
        if (thumbnailItems.length === 0) return;

        const firstIndex = Math.max(0, activeIndex - 2);
        const lastIndex = Math.min(thumbnailItems.length - 1, activeIndex + 2);

        for (let index = firstIndex; index <= lastIndex; index += 1) {
          this.hydrateThumbnailItem(thumbnailItems[index]);
        }
      }

      preloadAllGalleryImages() {
        if (this.context !== 'main-product') return;

        this.disconnectDeferredMediaObserver();
        this.disconnectDeferredThumbnailObserver();

        this.querySelectorAll('.product__media-item').forEach((mediaItem, index) => {
          mediaItem.querySelectorAll('img').forEach((image) => {
            image.loading = 'eager';
            if (index > 0) image.fetchPriority = 'low';
          });
          this.hydrateMediaItem(mediaItem);
        });

        this.querySelectorAll('.product__thumbs-item').forEach((thumbnailItem) => {
          thumbnailItem.querySelectorAll('img').forEach((image) => {
            image.loading = 'eager';
            image.fetchPriority = 'low';
          });
          this.hydrateThumbnailItem(thumbnailItem);
        });
      }

      observeDeferredThumbnails() {
        this.disconnectDeferredThumbnailObserver();

        const thumbnailItems = Array.from(this.querySelectorAll('.product__thumbs-item')).filter((thumbnailItem) =>
          thumbnailItem.querySelector('img[data-deferred-product-thumbnail="pending"]')
        );
        if (thumbnailItems.length === 0) return;

        if (!('IntersectionObserver' in window)) {
          thumbnailItems.forEach((thumbnailItem) => this.hydrateThumbnailItem(thumbnailItem));
          return;
        }

        this.deferredThumbnailObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this.hydrateThumbnailItem(entry.target);
              observer.unobserve(entry.target);
            });
          },
          {
            root: this.elements.thumbnails,
            rootMargin: '0px',
          }
        );

        thumbnailItems.forEach((thumbnailItem) => this.deferredThumbnailObserver.observe(thumbnailItem));
      }

      observeDeferredMedia() {
        this.disconnectDeferredMediaObserver();

        const mediaItems = Array.from(this.querySelectorAll('.product__media-item')).filter((mediaItem) =>
          mediaItem.querySelector('img[data-deferred-product-media="pending"]')
        );
        if (mediaItems.length === 0) return;

        if (!('IntersectionObserver' in window)) {
          mediaItems.forEach((mediaItem) => this.hydrateMediaItem(mediaItem));
          return;
        }

        this.deferredMediaObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this.hydrateMediaItem(entry.target);
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '300px 0px' }
        );

        mediaItems.forEach((mediaItem) => this.deferredMediaObserver.observe(mediaItem));
      }

      disconnectDeferredMediaObserver() {
        this.deferredMediaObserver?.disconnect();
        this.deferredMediaObserver = null;
      }

      disconnectDeferredThumbnailObserver() {
        this.deferredThumbnailObserver?.disconnect();
        this.deferredThumbnailObserver = null;
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider?.destroy();
          this.sliderInstance = false;
        }
        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider?.destroy();
          this.thumbsInstance = false;
        }
        if (this.sliderOptions) delete this.sliderOptions.thumbs;
      }

      initImageZoom() {
        this.destroyImageZoom();

        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

            let source = {
              id: mediaIndex,
              mediaType: mediaType,
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video':
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';
                const mediaElement = media.querySelector(htmlTag);
                if (!mediaElement) return;

                source = {
                  ...source,
                  html: `<div class="pswp__item--${mediaType}">${mediaElement.outerHTML}</div>`,
                };
                break;
              default: // Image.
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight,
                };
                break;
            }

            dataSource.push(source);
          });
        }

        if (!window.FoxTheme?.PhotoSwipeLightbox || !window.FoxTheme?.PhotoSwipe) return;

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            const activeSlide = slides[activeIndex];
            const image = activeSlide?.querySelector('img');
            if (image) return image;
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            const activeSlide = slides[activeIndex];
            const image = activeSlide?.querySelector('img');
            if (image) {
              if (image.currentSrc && !image.currentSrc.startsWith('data:')) return image.currentSrc;
              return activeSlide.dataset.src || image.dataset.deferredSrc || placeholderSrc;
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          const currentIndex = this.lightbox?.pswp?.currIndex;
          if (Number.isInteger(currentIndex)) {
            lastLightboxIndex = currentIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          const slider = this.sliderInstance?.slider;

          if (slider && lastLightboxIndex !== null) {
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                if (this.isConnected && this.sliderInstance?.slider === slider) {
                  slider.slideTo(targetIndex, 0);
                }
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: this.dataset.nextSlideLabel || 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: () => {
                this.lightbox?.pswp?.next();
              },
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: this.dataset.previousSlideLabel || 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: () => {
                this.lightbox?.pswp?.prev();
              },
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: this.dataset.closeLabel || 'Close',
            order: 2,
            isButton: true,
            html: '<svg class="icon icon-close icon--thick" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none"><path d="M6 6 18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path><path d="M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg>',
            onClick: () => {
              this.lightbox?.pswp?.close();
            },
          });
        });

        this.lightbox.init();

        this.removeZoomDelegate = FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const mediaItem = media.closest('.product__media-item');
              this.hydrateMediaItem(mediaItem);
              const index = Array.from(
                this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')
              ).indexOf(mediaItem);
              if (index < 0) return;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      destroyImageZoom() {
        if (typeof this.removeZoomDelegate === 'function') this.removeZoomDelegate();
        this.removeZoomDelegate = null;
        this.lightbox?.destroy();
        this.lightbox = null;
      }

      handleContentChanged() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        if (this.sliderInstance?.slider) {
          this.resetToCover();
        } else {
          this.observeDeferredMedia();
        }
        this.observeDeferredThumbnails();
        this.preloadAllGalleryImages();
        if (this.enableImageZoom) this.initImageZoom();
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;
          const activeSlide = slides[activeIndex];
          this.updateThumbnailState(activeIndex);

          if (activeSlide) {
            this.hydrateActiveMedia(swiper);
            const isModelMediaType = activeSlide.dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex } = swiper;
          const activeSlide = slides[activeIndex];
          this.updateThumbnailState(activeIndex);

          if (activeSlide) {
            this.hydrateActiveMedia(swiper);
            this.playActiveMedia(activeSlide);

            const isModelMediaType = activeSlide.dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      updateThumbnailState(activeIndex) {
        const thumbnails = Array.from(this.querySelectorAll('.product__thumbs-container'));
        const targetIndex = Math.max(0, Math.min(activeIndex, thumbnails.length - 1));

        thumbnails.forEach((thumbnail, index) => {
          if (index === targetIndex) {
            thumbnail.setAttribute('aria-current', 'true');
          } else {
            thumbnail.removeAttribute('aria-current');
          }
        });

        // Main-media changes can intentionally suppress Swiper callbacks (for
        // example, variant/format changes). Keep the thumbnail carousel in sync
        // here so arrows, swipes, thumbnails, and programmatic changes all move
        // the active thumbnail into view through one shared path.
        const thumbnailSlider = this.thumbsInstance?.slider || this.sliderInstance?.slider?.thumbs?.swiper;
        if (thumbnailSlider?.slides?.length && typeof thumbnailSlider.slideTo === 'function') {
          const sliderTargetIndex = Math.min(targetIndex, thumbnailSlider.slides.length - 1);
          thumbnailSlider.slideTo(sliderTargetIndex);
        }
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        if (!featuredMedia) return;

        if (this.sliderInstance?.slider) {
          const slideIndex = featuredMedia.position || 0;
          const targetIndex = Math.max(0, slideIndex - 1);
          const { slides } = this.sliderInstance.slider;
          this.hydrateMediaItem(slides[targetIndex - 1]);
          this.hydrateMediaItem(slides[targetIndex]);
          this.hydrateMediaItem(slides[targetIndex + 1]);
          this.hydrateNearbyThumbnails(targetIndex);
          this.sliderInstance.slider.slideTo(slideIndex - 1, 0, false);
          this.updateThumbnailState(targetIndex);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (String(media.dataset.mediaId) === String(featuredMedia.id)) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            this.hydrateMediaItem(selectedMedia);
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
